import { Storage } from "@faire/web--source/common/Storage";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import omit from "lodash/omit";

import {
  IssueId,
  SelfServeNodeLevel,
  SelfServeNodeLevelWithExtra,
  SelfServeNodeLevels,
} from "../types";

const SELECTED_ISSUES_KEY = "SELECTED_ISSUES_KEY";
const SELECTED_BRAND_ORDER = "SELECTED_BRAND_ORDER";
const STEP_KEY = "STEP_KEY";
const StorageService = Storage.local;
const SELECTED_NODE_KEY = "SELECTED_NODE_KEY";

export type StorageNodeValue = {
  [key in SelfServeNodeLevel]: IssueId;
};

export const clearLocalStorage = () => {
  StorageService.removeItem(SELECTED_ISSUES_KEY);
  StorageService.removeItem(SELECTED_BRAND_ORDER);
  StorageService.removeItem(STEP_KEY);
  StorageService.removeItem(SELECTED_NODE_KEY);
};

export const clearSelectedBrandOrderLocalStorage = () => {
  StorageService.removeItem(SELECTED_BRAND_ORDER);
};

export const clearNodesFromLocalStorage = () => {
  clearCurrentNodeLevelAndAboveFromLocalStorage(0);
};

export const clearCurrentNodeLevelAndAboveFromLocalStorage = (
  currentNodeLevel: SelfServeNodeLevelWithExtra
) => {
  const storageValue = StorageService.getItem(SELECTED_NODE_KEY);
  let currentNodeValue = {};
  if (storageValue) {
    currentNodeValue = JSON.parse(storageValue);
    for (let i = currentNodeLevel; i <= SelfServeNodeLevels.length; i++) {
      currentNodeValue = omit(currentNodeValue, i);
    }
  }
  StorageService.setItem(SELECTED_NODE_KEY, JSON.stringify(currentNodeValue));
};

export const setSelectedBrandOrderToLocalStorage = (
  brandOrder: ISelfServeBrandOrder
) => {
  StorageService.setItem(SELECTED_BRAND_ORDER, JSON.stringify(brandOrder));
};

export const getSelectedBrandOrderFromLocalStorage = ():
  | ISelfServeBrandOrder
  | undefined => {
  const selectedBrandOrder = StorageService.getItem(SELECTED_BRAND_ORDER);
  return selectedBrandOrder ? JSON.parse(selectedBrandOrder) : undefined;
};

/**
 * Set selected node to local storage
 * The reason we set nodeId instead of the entire SelfServeNode is that HTML element can't be stringified
 * @param selfServeNodeLevel
 * @param selfServeNodeId
 */
export const setSelectedNodeToLocalStorage = (
  selfServeNodeLevel: SelfServeNodeLevel,
  selfServeNodeId: IssueId | undefined
) => {
  clearCurrentNodeLevelAndAboveFromLocalStorage(selfServeNodeLevel);
  const nodesInLocalStorage: string =
    StorageService.getItem(SELECTED_NODE_KEY) ?? "{}";
  const parsedNodesInLocalStorage = JSON.parse(nodesInLocalStorage);
  const updatedNodes = JSON.stringify({
    ...parsedNodesInLocalStorage,
    [selfServeNodeLevel]: selfServeNodeId,
  });
  StorageService.setItem(SELECTED_NODE_KEY, updatedNodes);
};

export const getSelectedNodesFromLocalStorage = ():
  | StorageNodeValue
  | undefined => {
  const nodes = StorageService.getItem(SELECTED_NODE_KEY);
  return nodes ? (JSON.parse(nodes) as StorageNodeValue) : undefined;
};
